import React from "react";
import { motion } from "framer-motion";
import { Inline } from "./Inline";
import { Callout } from "./Callout";
import { DataTable } from "./DataTable";
import { Quiz } from "./Quiz";
import { LossCurve } from "./widgets/LossCurve";
import { EndpointPicker } from "./widgets/EndpointPicker";
import { OrderingDrill } from "./widgets/OrderingDrill";
import { MemoryPalace } from "./widgets/MemoryPalace";
import { MetricPicker } from "./widgets/MetricPicker";
import { StoragePicker } from "./widgets/StoragePicker";
import { IngestionPicker } from "./widgets/IngestionPicker";

const EASE = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

function Block({ block }) {
  switch (block.t) {
    case "h3":
      return (
        <Reveal>
          <h3 className="font-serif-display text-[26px] md:text-[30px] font-medium text-[#2F6F4F] mt-12 mb-3 leading-tight">
            {block.c}
          </h3>
        </Reveal>
      );
    case "p":
      return (
        <Reveal>
          <p className="text-[15.5px] leading-[1.75] text-[#1C2430]/85 my-4">
            <Inline text={block.c} />
          </p>
        </Reveal>
      );
    case "ul":
      return (
        <Reveal>
          <ul className="my-5 space-y-2.5">
            {block.items.map((it, i) => (
              <li key={i} className="relative pl-6 text-[15px] leading-relaxed text-[#1C2430]/85">
                <span className="absolute left-0 top-[10px] w-2 h-px bg-[#2F6F4F]" />
                <Inline text={it} />
              </li>
            ))}
          </ul>
        </Reveal>
      );
    case "pic":
    case "mn":
    case "rule":
      return <Callout variant={block.t} text={block.c} />;
    case "table":
      return <DataTable head={block.head} rows={block.rows} />;
    case "widget":
      if (block.w === "quiz") return <Quiz quizKey={block.key} />;
      if (block.w === "loss") return <LossCurve />;
      if (block.w === "endpoint") return <EndpointPicker />;
      if (block.w === "ordering") return <OrderingDrill />;
      if (block.w === "palace") return <MemoryPalace />;
      if (block.w === "metric") return <MetricPicker />;
      if (block.w === "storage") return <StoragePicker />;
      if (block.w === "ingest") return <IngestionPicker />;
      return null;
    default:
      return null;
  }
}

export function Chapter({ chapter, index }) {
  return (
    <section id={chapter.id} className="scroll-mt-20 py-16 md:py-24 border-t border-[var(--border-heavy)] first:border-t-0" data-testid={`chapter-${chapter.id}`}>
      <div className="flex items-baseline gap-5 mb-3">
        <motion.span
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="font-mono-ui text-[13px] text-[#2F6F4F] tabular-nums"
        >
          {chapter.num}
        </motion.span>
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#1C2430]/40">
          {chapter.kicker}
        </span>
      </div>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="font-serif-display text-[38px] md:text-[52px] leading-[0.98] font-medium tracking-tight text-[#1C2430] mb-8 max-w-3xl"
      >
        {chapter.title}
      </motion.h2>
      <div className="max-w-[780px]">
        {chapter.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </div>
    </section>
  );
}
